import { CreateDonationRequestCurrencyEnum, ProjectSettingsModeGroupEnum } from '@/app/api';
import { MenuItem } from '@/app/components/MainNavigation/MainNavigation';
import { Country, Language, PaymentMethod, Routes } from '@/app/enums';
import { mainNavigationItemsForCZ, mainNavigationItemsForSK } from '@/app/utils/countryConfig/mainNavigationItems';
import { envs } from '@/app/utils/envs';

import { czDobroFaq, czPlusFaq } from '@/app/utils/countryConfig/faqTexts/cs';
import { skDobroFaq, skPlusFaq } from '@/app/utils/countryConfig/faqTexts/sk';
import {
  footerNavigationSectionsForCZ,
  footerNavigationSectionsForSK,
} from '@/app/utils/countryConfig/footerNavigationSections';

interface FooterNavigationItem {
  titleTranslationKey: string;
  path: string;
}

export interface FooterNavigationSection {
  titleTranslationKey: string;
  items: FooterNavigationItem[];
}

interface CommercialProjectsEnabledSettings {
  enabled: true;
  availablePaymentMethods: PaymentMethod[];
  defaultPaymentMethod: PaymentMethod;
  canSelectPaymentMethod: boolean;
}

interface CharityCollectionsEnabledSettings {
  enabled: true;
  single: PaymentMethod[];
  longTerm: PaymentMethod[];
  defaultPaymentMethod: PaymentMethod;
  defaultPaymentMethodForLongTermProjects: PaymentMethod;
}

interface GoodHabitEnabledSettings {
  enabled: true;
  availablePaymentMethods: PaymentMethod[];
  defaultPaymentMethod: PaymentMethod;
}

interface GoodHabitDisabledSettings {
  enabled: false;
}

// For now we only support enabled: true but it can get handy to define another type later for Disabled CommercialProjects
interface CommercialProjectsSettings extends CommercialProjectsEnabledSettings {}

// For now we only support enabled: true but it can get handy to define another type later for Disabled CommercialProjects
interface CharityCollectionsSettings extends CharityCollectionsEnabledSettings {}

type GoodHabitSettings = GoodHabitEnabledSettings | GoodHabitDisabledSettings;

export enum SocialMenuItemm {
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  LINKEDIN = 'linkedin',
  YOUTUBE = 'youtube',
  PODCAST = 'podcast',
  X = 'x',
}

interface CountryConfig {
  socialNavigationItems: Array<{ id: SocialMenuItemm; link: string }>;
  ounwerShipNoteTranslationKey?: string;
  shareDialogExtraEarnValue: number;
  isSupportDonioBannerVisible: boolean;
  country: Country;
  fallbackLanguage: Language;
  currency: CreateDonationRequestCurrencyEnum;
  faqs: Record<ProjectSettingsModeGroupEnum, Array<[string, string]>>;
  donationFormCommon: {
    defaultTipForAmountBelowBreakpoint: number;
    defaultTipForAmountAboveBreakpoint: number;
    tipBreakpointForDefaultValue: number;
    donationAmountMinimum: number;
    donationAmountMaximum: number;
    donateByCardTotalAmountMaximum: number;
    phoneNumberFieldPlaceholder: string;
    isPrivacyConsentCheckboxSupported: boolean;
  };
  commercialProjects: CommercialProjectsSettings;
  goodHabit: GoodHabitSettings;
  charityCollections: CharityCollectionsSettings;
  mainNavigationItems: MenuItem[];
  footerNavigationSections: FooterNavigationSection[];
  organizationDefaultAgreedToShareContact: boolean;
  shouldDisplayProjectFootnoteText: boolean;
  minimalAmountForGoodHabitToView: number;
  amountBasedTipOptions: Array<{
    from: number;
    to: number;
    default: number;
    options: Array<{ value: number; noteTranslationKey?: string; label?: string }>;
  }>;
  collectionDonationSettings: {
    isCountryRequired: boolean;
    isDateOfBirthRequired: boolean;
    isAdditionalInfoRequiredForDonationReceipt?: boolean;
    shouldBeDonationReceiptCheckboxBeforeGoodnessCertificate: boolean;
    displayDonationAsLegalEntityCheckbox?: boolean;
    displayWhatYouGetLabel?: boolean;
    isTinRequiredForDonationReceipt: boolean;
    displayNewLawDisclaimer?: boolean;
  };
}

const czConfig: CountryConfig = {
  collectionDonationSettings: {
    isCountryRequired: false,
    isDateOfBirthRequired: false,
    isAdditionalInfoRequiredForDonationReceipt: true,
    shouldBeDonationReceiptCheckboxBeforeGoodnessCertificate: false,
    displayDonationAsLegalEntityCheckbox: false,
    displayWhatYouGetLabel: true,
    isTinRequiredForDonationReceipt: false,
    displayNewLawDisclaimer: false,
  },
  amountBasedTipOptions: [
    {
      from: 10,
      to: 150,
      default: 20,
      options: [
        { value: 0, noteTranslationKey: 'components.TipForDonioInput.weKeepNothing' },
        { value: 10, label: '10Kč', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel1' },
        { value: 15, label: '15Kč', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel2' },
        { value: 20, label: '20Kč', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel3' },
        { value: 30, label: '30Kč', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel4' },
      ],
    },
    {
      from: 151,
      to: 250,
      default: 30,
      options: [
        { value: 0, noteTranslationKey: 'components.TipForDonioInput.weKeepNothing' },
        { value: 10, label: '10Kč', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel1' },
        { value: 20, label: '20Kč', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel2' },
        { value: 30, label: '30Kč', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel3' },
        { value: 40, label: '40Kč', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel4' },
      ],
    },
    {
      from: 251,
      to: 400,
      default: 40,
      options: [
        { value: 0, noteTranslationKey: 'components.TipForDonioInput.weKeepNothing' },
        { value: 20, label: '20Kč', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel1' },
        { value: 30, label: '30Kč', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel2' },
        { value: 40, label: '40Kč', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel3' },
        { value: 50, label: '50Kč', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel4' },
      ],
    },
  ],
  socialNavigationItems: [
    {
      id: SocialMenuItemm.FACEBOOK,
      link: 'https://www.facebook.com/Doniocz/',
    },
    {
      id: SocialMenuItemm.INSTAGRAM,
      link: 'https://www.instagram.com/doniocz/',
    },
    {
      id: SocialMenuItemm.LINKEDIN,
      link: 'https://cz.linkedin.com/company/donio',
    },
    {
      id: SocialMenuItemm.YOUTUBE,
      link: 'https://www.youtube.com/channel/UCXv5UNMPDNVTweLfn-Dc6sA',
    },
    {
      id: SocialMenuItemm.PODCAST,
      link: `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.BLOG}`,
    },
    {
      id: SocialMenuItemm.X,
      link: 'https://x.com/doniocz',
    },
  ],
  ounwerShipNoteTranslationKey: 'components.Footer.ownershipNote',
  faqs: {
    [ProjectSettingsModeGroupEnum.Dobro]: czDobroFaq,
    [ProjectSettingsModeGroupEnum.Plus]: czPlusFaq,
  },
  organizationDefaultAgreedToShareContact: false,
  shareDialogExtraEarnValue: 800,
  isSupportDonioBannerVisible: false,
  country: Country.CZECH_REPUBLIC,
  minimalAmountForGoodHabitToView: 1000,
  fallbackLanguage: Language.CZ,
  currency: CreateDonationRequestCurrencyEnum.Czk,
  shouldDisplayProjectFootnoteText: true,
  donationFormCommon: {
    defaultTipForAmountBelowBreakpoint: 11,
    defaultTipForAmountAboveBreakpoint: 5,
    tipBreakpointForDefaultValue: 4999,
    donationAmountMinimum: 10,
    donationAmountMaximum: 10_000_000,
    donateByCardTotalAmountMaximum: 1_000_000,
    phoneNumberFieldPlaceholder: '+420XXXXXXXXX',
    isPrivacyConsentCheckboxSupported: true,
  },

  charityCollections: {
    enabled: true,
    longTerm: [PaymentMethod.WireTransfer, PaymentMethod.Card, PaymentMethod.QrCode],
    single: [PaymentMethod.Card, PaymentMethod.WireTransfer, PaymentMethod.QrCode],
    defaultPaymentMethod: PaymentMethod.Card,
    defaultPaymentMethodForLongTermProjects: PaymentMethod.WireTransfer,
  },

  goodHabit: {
    enabled: true,
    availablePaymentMethods: [PaymentMethod.Card, PaymentMethod.WireTransfer, PaymentMethod.QrCode],
    defaultPaymentMethod: PaymentMethod.Card,
  },

  commercialProjects: {
    canSelectPaymentMethod: false,
    enabled: true,
    availablePaymentMethods: [PaymentMethod.Card],
    defaultPaymentMethod: PaymentMethod.Card,
  },

  mainNavigationItems: mainNavigationItemsForCZ,

  footerNavigationSections: footerNavigationSectionsForCZ,
};

const skConfig: CountryConfig = {
  collectionDonationSettings: {
    isCountryRequired: true,
    isDateOfBirthRequired: true,
    isAdditionalInfoRequiredForDonationReceipt: false,
    shouldBeDonationReceiptCheckboxBeforeGoodnessCertificate: true,
    displayDonationAsLegalEntityCheckbox: true,
    displayWhatYouGetLabel: false,
    isTinRequiredForDonationReceipt: true,
    displayNewLawDisclaimer: true,
  },
  amountBasedTipOptions: [
    {
      from: 1,
      to: 15,
      default: 2,
      options: [
        { value: 0, noteTranslationKey: 'components.TipForDonioInput.weKeepNothing' },
        { value: 1, label: '1€', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel1' },
        { value: 2, label: '2€', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel3' },
        { value: 3, label: '3€', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel4' },
      ],
    },
    {
      from: 16,
      to: 25,
      default: 3,
      options: [
        { value: 0, noteTranslationKey: 'components.TipForDonioInput.weKeepNothing' },
        { value: 1, label: '1€', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel1' },
        { value: 2, label: '2€', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel2' },
        { value: 3, label: '3€', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel3' },
        { value: 5, label: '5€', noteTranslationKey: 'components.TipForDonioInput.amountTipLevel4' },
      ],
    },
  ],
  socialNavigationItems: [
    {
      id: SocialMenuItemm.FACEBOOK,
      link: 'https://www.facebook.com/donioslovensko/',
    },
    {
      id: SocialMenuItemm.INSTAGRAM,
      link: 'https://www.instagram.com/donio.sk/',
    },
    {
      id: SocialMenuItemm.LINKEDIN,
      link: 'https://www.linkedin.com/company/donio-slovensko',
    },
    {
      id: SocialMenuItemm.PODCAST,
      link: `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${Routes.BLOG}`,
    },
  ],
  faqs: {
    [ProjectSettingsModeGroupEnum.Dobro]: skDobroFaq,
    [ProjectSettingsModeGroupEnum.Plus]: skPlusFaq,
  },
  organizationDefaultAgreedToShareContact: true,
  shareDialogExtraEarnValue: 32,
  isSupportDonioBannerVisible: false,
  minimalAmountForGoodHabitToView: 50,
  country: Country.SLOVAKIA,
  fallbackLanguage: Language.SK,
  currency: CreateDonationRequestCurrencyEnum.Eur,
  shouldDisplayProjectFootnoteText: false,
  donationFormCommon: {
    defaultTipForAmountBelowBreakpoint: 11,
    defaultTipForAmountAboveBreakpoint: 5,
    tipBreakpointForDefaultValue: 199,
    donationAmountMinimum: 1,
    donationAmountMaximum: 399_770,
    donateByCardTotalAmountMaximum: 40_000,
    phoneNumberFieldPlaceholder: '+421XXXXXXXXX',
    isPrivacyConsentCheckboxSupported: false,
  },

  charityCollections: {
    enabled: true,
    longTerm: [PaymentMethod.ApplePay, PaymentMethod.WireTransfer, PaymentMethod.Card, PaymentMethod.QrCode],
    single: [PaymentMethod.ApplePay, PaymentMethod.Card, PaymentMethod.WireTransfer, PaymentMethod.QrCode],
    defaultPaymentMethod: PaymentMethod.Card,
    defaultPaymentMethodForLongTermProjects: PaymentMethod.WireTransfer,
  },

  goodHabit: {
    enabled: false,
  },

  commercialProjects: {
    enabled: true,
    canSelectPaymentMethod: true,
    availablePaymentMethods: [PaymentMethod.Card, PaymentMethod.WireTransfer, PaymentMethod.QrCode],
    defaultPaymentMethod: PaymentMethod.Card,
  },

  mainNavigationItems: mainNavigationItemsForSK,

  footerNavigationSections: footerNavigationSectionsForSK,
};

export const getCountryConfig = () => {
  const country = envs.country;

  switch (country) {
    case Country.CZECH_REPUBLIC:
      return czConfig;

    case Country.SLOVAKIA:
      return skConfig;

    default:
      throw new Error(`Config not found for country "${country}"`);
  }
};

export const countryConfig = getCountryConfig();
